// @flow
import type { Node } from 'react';

export type EmailMessages = Array<{
  email: string,
  message: string,
}>;

export const roles = Object.freeze({
  admin: 'admin',
  member: 'member',
  owner: 'owner',
});

export type RoleType = $Keys<typeof roles>;

export type EmailInviterInviteProp = (
  emails: Array<string>,
  role: ?RoleType
) => void;

export const memberStatus = Object.freeze({
  invitation: 'invitation',
  member: 'member',
  request: 'request',
});

export type MemberStatusType = $Keys<typeof memberStatus>;

export type OnChooseActionDataType = {
  email: string,
  id: number,
  name: string,
  role: RoleType,
};

export type OnChooseActionType = (
  action: string,
  data: OnChooseActionDataType
) => void;

export type MemberUsageType = {
  numTasks: number,
  numZaps: number,
};

export type MemberType = {
  canEdit: boolean,
  email: string,
  emailHash: string,
  id: string,
  isCurrentUser: boolean,
  name: string,
  recordId: number,
  role: RoleType,
  status: MemberStatusType,
  usage: MemberUsageType,
};

export type InvitationType = {
  createdAt: string,
  email: string,
  emailHash: string,
  id: number,
  isAccessRequest: boolean,
  role: RoleType,
  status: string,
};

export const accessPolicyLevels = Object.freeze({
  auto_join: 'auto_join',
  invite_only: 'invite_only',
  request_access: 'request_access',
});

export type AccessPolicyLevel = $Keys<typeof accessPolicyLevels>;

export type AccountChoiceType = {
  accessRequested?: boolean,
  canJoin?: boolean,
  href?: string,
  id: number,
  imageUrl: string,
  isActive?: boolean,
  isEmailVerificationRequired?: boolean,
  isMember?: boolean,
  name: string,
  role?: RoleType,
};

export type AccessPolicyInput = {
  accessLevel: AccessPolicyLevel,
  isEmailVerificationRequired: boolean,
};

export type OnAccessPolicyChangeType = AccessPolicyInput => void;

export type TeamMemberType = {
  avatarUrl: ?string,
  email: string,
  id: number,
  name: ?string,
  userId: number,
};

export type TeamType = {
  // used for uploading image files
  avatarFile: ?File,
  avatarUrl: ?string,
  canEdit: boolean,
  description: ?string,
  id: number,
  memberCount: number,
  memberPreview: Array<TeamMemberType>,
  members: ?Array<TeamMemberType>,
  name: string,
};

export type OnSearchMembersType = string => Promise<Array<TeamMemberType>>;

export type SharedMemberObjectType = 'member' | 'team';

export type SharedItemType = {
  icon?: string,
  id: string | number,
  image?: Node,
  imageUrl?: string,
  isShared: boolean,
  isSharable?: boolean,
  name: string,
};

export type ShareMemberRoleType = RoleType | 'shareOwner';

export type ShareMemberType = {
  description: string,
  id: string | number,
  imageUrl?: string,
  name: string,
  role: ShareMemberRoleType,
  type: SharedMemberObjectType,
};

export type SharedWithType = Array<ShareMemberType>;

export type FolderIdType = number | string;

export type FolderType = {
  id: FolderIdType,
  sharedWith: Array<ShareMemberType>,
};

export type AuthType = {
  id: string,
  sharedWith: Array<ShareMemberType>,
};

export type OnToggleAllType = ({
  folderId: FolderIdType,
  shouldShare: boolean,
}) => void;

export type OnToggleShareType = ({
  id: string | number,
  folderId: FolderIdType,
  shouldShare: boolean,
  type: SharedMemberObjectType,
}) => void;

export type OnSearchShareMemberType = (
  search: string
) => Promise<Array<ShareMemberType>>;
